import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  Fade,
  IconButton,
  Tooltip,
  Button,
  TextField,
  Chip,
  Switch,
  FormControlLabel,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  alpha,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  List,
  ListItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Save,
  Refresh,
  DragIndicator,
  Edit,
  Delete,
  Visibility,
  VisibilityOff,
  Add,
  ContentCopy,
  MoreVert,
  Settings,
  ArrowBack,
  ArrowForward,
  Code,
  Check,
  Close,
  SwapVert,
  CloudDownload,
  CheckBox,
  CheckBoxOutlineBlank,
  Preview,
  TableChart,
  TrendingUp,
  ArrowUpward,
  ArrowDownward,
  OpenInNew,
  Warning,
  CheckCircle,
  Error,
  AddCircle,
  Healing,
} from '@mui/icons-material';
import LinearProgress from '@mui/material/LinearProgress';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import PageHeader from '../components/common/PageHeader';
import { PageLoader } from '../components/common/LoadingState';
import WidgetPreview from '../components/widgets/WidgetPreview';
import api from '../services/api';

const WidgetEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Widget data from API
  const [widgetData, setWidgetData] = useState(null);
  const [products, setProducts] = useState([]);
  const [availableSpecs, setAvailableSpecs] = useState([]);
  const [comparisonConfig, setComparisonConfig] = useState({
    visibleSpecs: [],
    specsOrder: [],
    specsLabels: {},
    hiddenSpecs: []
  });
  
  // UI state
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [specsDialogOpen, setSpecsDialogOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [copySnackbar, setCopySnackbar] = useState(false);
  
  // View mode state
  const [viewMode, setViewMode] = useState('editor'); // 'editor' or 'preview'
  
  // Import specs state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importStep, setImportStep] = useState('select'); // 'select', 'loading', 'fields', 'applying'
  const [selectedSkusForImport, setSelectedSkusForImport] = useState([]);
  const [importJobId, setImportJobId] = useState(null);
  const [importProgress, setImportProgress] = useState(0);
  const [importFields, setImportFields] = useState([]);
  const [importFieldExamples, setImportFieldExamples] = useState({});
  const [selectedImportFields, setSelectedImportFields] = useState([]);
  
  // Upselling recommendations state
  const [upsellingMap, setUpsellingMap] = useState({});
  const [upsellingDialogOpen, setUpsellingDialogOpen] = useState(false);
  const [editingUpsellingProduct, setEditingUpsellingProduct] = useState(null);
  const [editingRecommendations, setEditingRecommendations] = useState([]);
  
  // Specs CRUD state
  const [addSpecDialogOpen, setAddSpecDialogOpen] = useState(false);
  const [editSpecDialogOpen, setEditSpecDialogOpen] = useState(false);
  const [specFormData, setSpecFormData] = useState({ key: '', value: '', type: 'string' });
  const [editingSpecProduct, setEditingSpecProduct] = useState(null);
  const [editingSpecKey, setEditingSpecKey] = useState(null);
  
  // Add new comparison row state
  const [addRowDialogOpen, setAddRowDialogOpen] = useState(false);
  const [newRowData, setNewRowData] = useState({ 
    key: '', 
    label: '', 
    productValues: {} // { sku: value, sku2: value2, ... }
  });

  // Fetch widget data
  const fetchWidgetData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/widgets/data/${id}`);
      const data = response.data;
      
      setWidgetData(data.widget);
      setProducts(data.products);
      setAvailableSpecs(data.availableSpecs);
      setComparisonConfig(data.comparisonConfig || {
        visibleSpecs: [],
        specsOrder: [],
        specsLabels: {},
        hiddenSpecs: []
      });
      setUpsellingMap(data.upsellingMap || {});
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cargar el widget');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchWidgetData();
  }, [fetchWidgetData]);

  // Get visible specs in order
  const getOrderedSpecs = () => {
    const { visibleSpecs, specsOrder, hiddenSpecs } = comparisonConfig;
    
    // If no visible specs configured, show all except hidden
    let specsToShow = visibleSpecs.length > 0 
      ? visibleSpecs 
      : availableSpecs.map(s => s.key).filter(k => !hiddenSpecs.includes(k));
    
    // Sort by specsOrder if available
    if (specsOrder.length > 0) {
      specsToShow = [...specsToShow].sort((a, b) => {
        const orderA = specsOrder.indexOf(a);
        const orderB = specsOrder.indexOf(b);
        if (orderA === -1 && orderB === -1) return 0;
        if (orderA === -1) return 1;
        if (orderB === -1) return -1;
        return orderA - orderB;
      });
    }
    
    return specsToShow;
  };

  // Get spec label
  const getSpecLabel = (key) => {
    if (comparisonConfig.specsLabels[key]) {
      return comparisonConfig.specsLabels[key];
    }
    const spec = availableSpecs.find(s => s.key === key);
    return spec?.label || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Format spec value for display
  const formatSpecValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Si' : 'No';
    if (typeof value === 'object') {
      if (value.negro && value.color) {
        return `Negro: ${value.negro}, Color: ${value.color}`;
      }
      return JSON.stringify(value);
    }
    return String(value);
  };

  // Handle product reorder (for drag-and-drop in specs)
  const handleProductReorder = async (result) => {
    if (!result.destination) return;
    
    const newProducts = Array.from(products);
    const [reorderedItem] = newProducts.splice(result.source.index, 1);
    newProducts.splice(result.destination.index, 0, reorderedItem);
    
    setProducts(newProducts);
    
    try {
      await api.patch(`/widgets/${id}/products/reorder`, {
        skuOrder: newProducts.map(p => p.sku)
      });
      setSuccess('Orden actualizado');
    } catch (err) {
      setError('Error al actualizar orden');
      fetchWidgetData(); // Revert on error
    }
  };

  // Handle move product left/right
  const handleMoveProduct = async (sku, direction) => {
    const currentIndex = products.findIndex(p => p.sku === sku);
    const newIndex = currentIndex + direction;
    
    if (newIndex < 0 || newIndex >= products.length) return;
    
    const newProducts = Array.from(products);
    [newProducts[currentIndex], newProducts[newIndex]] = [newProducts[newIndex], newProducts[currentIndex]];
    
    setProducts(newProducts);
    setMenuAnchor(null);
    
    try {
      await api.patch(`/widgets/${id}/products/reorder`, {
        skuOrder: newProducts.map(p => p.sku)
      });
      setSuccess('Orden actualizado');
    } catch (err) {
      setError('Error al actualizar orden');
      fetchWidgetData(); // Revert on error
    }
  };

  // Handle spec reorder
  const handleSpecReorder = async (result) => {
    if (!result.destination) return;
    
    const orderedSpecs = getOrderedSpecs();
    const newOrder = Array.from(orderedSpecs);
    const [reorderedItem] = newOrder.splice(result.source.index, 1);
    newOrder.splice(result.destination.index, 0, reorderedItem);
    
    const newConfig = {
      ...comparisonConfig,
      specsOrder: newOrder
    };
    
    setComparisonConfig(newConfig);
    
    try {
      await api.put(`/widgets/${id}/comparison-config`, newConfig);
      setSuccess('Orden de specs actualizado');
    } catch (err) {
      setError('Error al actualizar orden de specs');
    }
  };

  // Handle cell edit start
  const handleEditStart = (productSku, field, currentValue) => {
    setEditingCell({ sku: productSku, field });
    setEditValue(formatSpecValue(currentValue));
  };

  // Handle cell edit save
  const handleEditSave = async () => {
    if (!editingCell) return;
    
    const { sku, field } = editingCell;
    const isSpecField = field.startsWith('specs.');
    const fieldKey = isSpecField ? field : field;
    
    try {
      await api.patch(`/widgets/${id}/product/${sku}/override`, {
        field: fieldKey,
        value: editValue
      });
      
      // Update local state
      setProducts(products.map(p => {
        if (p.sku === sku) {
          if (isSpecField) {
            const specKey = field.replace('specs.', '');
            return {
              ...p,
              specs: { ...p.specs, [specKey]: editValue }
            };
          }
          return { ...p, [field]: editValue };
        }
        return p;
      }));
      
      setSuccess('Valor actualizado');
    } catch (err) {
      setError('Error al actualizar valor');
    } finally {
      setEditingCell(null);
      setEditValue('');
    }
  };

  // Handle cell edit cancel
  const handleEditCancel = () => {
    setEditingCell(null);
    setEditValue('');
  };

  // Toggle spec visibility
  const toggleSpecVisibility = async (specKey) => {
    const { hiddenSpecs = [] } = comparisonConfig;
    const isHidden = hiddenSpecs.includes(specKey);
    
    const newHiddenSpecs = isHidden
      ? hiddenSpecs.filter(k => k !== specKey)
      : [...hiddenSpecs, specKey];
    
    const newConfig = {
      ...comparisonConfig,
      hiddenSpecs: newHiddenSpecs
    };
    
    setComparisonConfig(newConfig);
    
    try {
      await api.put(`/widgets/${id}/comparison-config`, newConfig);
    } catch (err) {
      setError('Error al actualizar visibilidad');
    }
  };

  // Update spec label
  const updateSpecLabel = async (specKey, newLabel) => {
    const newConfig = {
      ...comparisonConfig,
      specsLabels: {
        ...comparisonConfig.specsLabels,
        [specKey]: newLabel
      }
    };
    
    setComparisonConfig(newConfig);
    
    try {
      await api.put(`/widgets/${id}/comparison-config`, newConfig);
      setSuccess('Label actualizado');
    } catch (err) {
      setError('Error al actualizar label');
    }
  };

  // Remove product from widget
  const handleRemoveProduct = async (sku) => {
    try {
      await api.delete(`/widgets/${id}/products/${sku}`);
      setProducts(products.filter(p => p.sku !== sku));
      setSuccess('Producto eliminado del widget');
    } catch (err) {
      setError('Error al eliminar producto');
    }
    setMenuAnchor(null);
  };

  // Copy endpoint
  const copyEndpoint = () => {
    const endpoint = `${window.location.origin}/api/widgets/data/${id}`;
    navigator.clipboard.writeText(endpoint);
    setCopySnackbar(true);
  };

  // Open import dialog
  const openImportDialog = () => {
    setImportDialogOpen(true);
    setImportStep('select');
    setSelectedSkusForImport(products.map(p => p.sku));
    setImportJobId(null);
    setImportProgress(0);
    setImportFields([]);
    setImportFieldExamples({});
    setSelectedImportFields([]);
  };

  // Start import specs
  const startImportSpecs = async () => {
    if (selectedSkusForImport.length === 0) {
      setError('Selecciona al menos un SKU para importar');
      return;
    }

    setImportStep('loading');
    setImportProgress(0);

    try {
      const response = await api.post(`/widgets/${id}/import-specs`, {
        skus: selectedSkusForImport,
        lang: 'PT'
      });

      const jobId = response.data.jobId;
      setImportJobId(jobId);

      // Poll for progress
      pollImportProgress(jobId);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al iniciar importación');
      setImportStep('select');
    }
  };

  // Poll import progress
  const pollImportProgress = async (jobId) => {
    try {
      const response = await api.get(`/widgets/${id}/import-specs/${jobId}`);
      const job = response.data;

      setImportProgress(job.progress);

      if (job.status === 'completed') {
        setImportFields(job.allFields || []);
        setImportFieldExamples(job.fieldExamples || {});
        setImportStep('fields');
        
        // Pre-select common useful fields
        const usefulFields = job.allFields.filter(f => 
          !['sku', 'id', 'createdAt', 'updatedAt', '_id'].includes(f)
        );
        setSelectedImportFields(usefulFields.slice(0, 15));
      } else if (job.status === 'processing') {
        // Continue polling
        setTimeout(() => pollImportProgress(jobId), 1000);
      } else {
        setError('Error en la importación');
        setImportStep('select');
      }
    } catch (err) {
      setError('Error al verificar progreso');
      setImportStep('select');
    }
  };

  // Finalize import - apply selected fields
  const finalizeImport = async () => {
    if (selectedImportFields.length === 0) {
      setError('Selecciona al menos un campo para importar');
      return;
    }

    setImportStep('applying');

    try {
      await api.post(`/widgets/${id}/import-specs/${importJobId}/finalize`, {
        selectedFields: selectedImportFields,
        applyToProducts: true
      });

      setSuccess(`Specs importados exitosamente para ${selectedSkusForImport.length} productos`);
      setImportDialogOpen(false);
      
      // Refresh widget data to show new specs
      fetchWidgetData();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al aplicar specs');
      setImportStep('fields');
    }
  };

  // Cancel import
  const cancelImport = async () => {
    if (importJobId) {
      try {
        await api.delete(`/widgets/${id}/import-specs/${importJobId}`);
      } catch (err) {
        // Ignore error on cancel
      }
    }
    setImportDialogOpen(false);
  };

  // Toggle SKU selection for import
  const toggleSkuForImport = (sku) => {
    if (selectedSkusForImport.includes(sku)) {
      setSelectedSkusForImport(selectedSkusForImport.filter(s => s !== sku));
    } else {
      setSelectedSkusForImport([...selectedSkusForImport, sku]);
    }
  };

  // Toggle field selection for import
  const toggleFieldForImport = (field) => {
    if (selectedImportFields.includes(field)) {
      setSelectedImportFields(selectedImportFields.filter(f => f !== field));
    } else {
      if (selectedImportFields.length >= 60) {
        setError('Máximo 60 campos permitidos');
        return;
      }
      setSelectedImportFields([...selectedImportFields, field]);
    }
  };

  // Select/deselect all SKUs
  const toggleAllSkus = () => {
    if (selectedSkusForImport.length === products.length) {
      setSelectedSkusForImport([]);
    } else {
      setSelectedSkusForImport(products.map(p => p.sku));
    }
  };

  // Upselling recommendations functions
  const openUpsellingDialog = (product) => {
    setEditingUpsellingProduct(product);
    setEditingRecommendations(upsellingMap[product.sku] || []);
    setUpsellingDialogOpen(true);
  };

  const toggleRecommendation = (sku) => {
    if (editingRecommendations.includes(sku)) {
      setEditingRecommendations(editingRecommendations.filter(s => s !== sku));
    } else {
      setEditingRecommendations([...editingRecommendations, sku]);
    }
  };

  const saveRecommendations = async () => {
    try {
      setSaving(true);
      await api.put(`/widgets/${id}/upselling-map/${editingUpsellingProduct.sku}`, {
        recommendations: editingRecommendations
      });
      
      setUpsellingMap(prev => ({
        ...prev,
        [editingUpsellingProduct.sku]: editingRecommendations
      }));
      
      setSuccess(`Recomendaciones para ${editingUpsellingProduct.title} guardadas`);
      setUpsellingDialogOpen(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar recomendaciones');
    } finally {
      setSaving(false);
    }
  };

  const moveRecommendation = (index, direction) => {
    const newOrder = [...editingRecommendations];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newOrder.length) return;
    [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];
    setEditingRecommendations(newOrder);
  };

  // Specs completeness helper functions
  const getAllSpecKeys = () => {
    const allKeys = new Set();
    products.forEach(product => {
      Object.keys(product.specs || {}).forEach(key => allKeys.add(key));
    });
    return Array.from(allKeys).sort();
  };

  const getProductSpecCompleteness = (product) => {
    const allKeys = getAllSpecKeys();
    const productSpecs = product.specs || {};
    const hasValue = (val) => val !== null && val !== undefined && val !== '' && val !== 'NOT_FOUND';
    
    const complete = allKeys.filter(key => hasValue(productSpecs[key]));
    const missing = allKeys.filter(key => !hasValue(productSpecs[key]));
    
    return {
      total: allKeys.length,
      complete: complete.length,
      missing: missing.length,
      missingKeys: missing,
      completeKeys: complete,
      percentage: allKeys.length > 0 ? Math.round((complete.length / allKeys.length) * 100) : 100
    };
  };

  const getSpecCoverage = (specKey) => {
    const hasValue = (val) => val !== null && val !== undefined && val !== '' && val !== 'NOT_FOUND';
    const productsWithSpec = products.filter(p => hasValue(p.specs?.[specKey]));
    return {
      count: productsWithSpec.length,
      total: products.length,
      percentage: products.length > 0 ? Math.round((productsWithSpec.length / products.length) * 100) : 0,
      missingProducts: products.filter(p => !hasValue(p.specs?.[specKey]))
    };
  };

  // Open add spec dialog
  const openAddSpecDialog = (product, suggestedKey = '') => {
    setEditingSpecProduct(product);
    setSpecFormData({ key: suggestedKey, value: '', type: 'string' });
    setAddSpecDialogOpen(true);
  };

  // Open edit spec dialog
  const openEditSpecDialog = (product, specKey) => {
    const currentValue = product.specs?.[specKey];
    let type = 'string';
    let value = currentValue;
    
    if (typeof currentValue === 'boolean') {
      type = 'boolean';
      value = currentValue ? 'true' : 'false';
    } else if (typeof currentValue === 'object' && currentValue !== null) {
      type = 'object';
      value = JSON.stringify(currentValue, null, 2);
    } else {
      value = String(currentValue ?? '');
    }
    
    setEditingSpecProduct(product);
    setEditingSpecKey(specKey);
    setSpecFormData({ key: specKey, value, type });
    setEditSpecDialogOpen(true);
  };

  // Add new comparison row (spec to all products)
  const addComparisonRow = async () => {
    if (!newRowData.key.trim()) {
      setError('El nombre del campo es requerido');
      return;
    }
    
    const key = newRowData.key.trim().toLowerCase().replace(/\s+/g, '_');
    const label = newRowData.label.trim() || newRowData.key.trim();
    
    try {
      setSaving(true);
      
      // Add spec to each product with its individual value
      for (const product of products) {
        const value = newRowData.productValues[product.sku] || '';
        await api.patch(`/widgets/${id}/product/${product.sku}/override`, {
          field: `specs.${key}`,
          value: value
        });
      }
      
      // Update comparison config with the new label
      const newConfig = {
        ...comparisonConfig,
        specsLabels: {
          ...comparisonConfig.specsLabels,
          [key]: label
        },
        visibleSpecs: [...(comparisonConfig.visibleSpecs || []), key]
      };
      
      await api.put(`/widgets/${id}/comparison-config`, newConfig);
      
      // Update local state with individual values
      setProducts(prev => prev.map(p => ({
        ...p,
        specs: {
          ...p.specs,
          [key]: newRowData.productValues[p.sku] || ''
        }
      })));
      
      setComparisonConfig(newConfig);
      
      // Add to availableSpecs if not exists
      if (!availableSpecs.find(s => s.key === key)) {
        setAvailableSpecs(prev => [...prev, { key, label, type: 'string' }]);
      }
      
      setSuccess(`Nueva fila "${label}" agregada a todos los productos`);
      setAddRowDialogOpen(false);
      setNewRowData({ key: '', label: '', productValues: {} });
    } catch (err) {
      setError(err.response?.data?.error || 'Error al agregar fila');
    } finally {
      setSaving(false);
    }
  };

  // Save spec (add or edit)
  const saveSpec = async (isNew = false) => {
    try {
      setSaving(true);
      
      let finalValue = specFormData.value;
      if (specFormData.type === 'boolean') {
        finalValue = specFormData.value === 'true';
      } else if (specFormData.type === 'object') {
        try {
          finalValue = JSON.parse(specFormData.value);
        } catch {
          setError('JSON inválido para el valor del objeto');
          setSaving(false);
          return;
        }
      }
      
      const field = `specs.${specFormData.key}`;
      
      await api.patch(`/widgets/${id}/product/${editingSpecProduct.sku}/override`, {
        field,
        value: finalValue
      });
      
      // Update local state
      setProducts(prev => prev.map(p => {
        if (p.sku === editingSpecProduct.sku) {
          return {
            ...p,
            specs: {
              ...p.specs,
              [specFormData.key]: finalValue
            }
          };
        }
        return p;
      }));
      
      setSuccess(`Spec "${specFormData.key}" ${isNew ? 'agregada' : 'actualizada'} para ${editingSpecProduct.title}`);
      setAddSpecDialogOpen(false);
      setEditSpecDialogOpen(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar spec');
    } finally {
      setSaving(false);
    }
  };

  // Render editable cell
  const renderEditableCell = (product, field, value) => {
    const isEditing = editingCell?.sku === product.sku && editingCell?.field === field;
    
    if (isEditing) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TextField
            size="small"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            autoFocus
            onKeyPress={(e) => {
              if (e.key === 'Enter') handleEditSave();
              if (e.key === 'Escape') handleEditCancel();
            }}
            sx={{ minWidth: 100 }}
          />
          <IconButton size="small" onClick={handleEditSave} color="primary">
            <Check fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={handleEditCancel}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
      );
    }
    
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          '&:hover .edit-icon': { opacity: 1 }
        }}
        onClick={() => handleEditStart(product.sku, field, value)}
      >
        <Typography variant="body2" sx={{ flex: 1 }}>
          {formatSpecValue(value)}
        </Typography>
        <Edit
          className="edit-icon"
          sx={{ fontSize: 14, ml: 0.5, opacity: 0, transition: 'opacity 0.2s', color: 'text.secondary' }}
        />
      </Box>
    );
  };

  if (loading) {
    return <PageLoader message="Cargando editor de widget..." />;
  }

  if (!widgetData) {
    return (
      <Box>
        <Alert severity="error">Widget no encontrado</Alert>
        <Button onClick={() => navigate('/widgets')} sx={{ mt: 2 }}>
          Volver a Widgets
        </Button>
      </Box>
    );
  }

  const orderedSpecs = getOrderedSpecs();

  return (
    <Box>
      <PageHeader
        title={`Editor: ${widgetData.name}`}
        subtitle={`${widgetData.retailName} - ${products.length} productos`}
        showBackButton
        backPath="/widgets"
        breadcrumbs={[
          { label: 'Widgets', path: '/widgets' },
          { label: 'Editor' }
        ]}
      >
        <Tooltip title="Importar Specs">
          <IconButton onClick={openImportDialog} color="secondary">
            <CloudDownload />
          </IconButton>
        </Tooltip>
        <Tooltip title="Copiar Endpoint">
          <IconButton onClick={copyEndpoint}>
            <ContentCopy />
          </IconButton>
        </Tooltip>
        <Tooltip title="Ver JSON">
          <IconButton onClick={() => window.open(`/api/widgets/data/${id}`, '_blank')}>
            <Code />
          </IconButton>
        </Tooltip>
        <Tooltip title="Configurar Specs">
          <IconButton onClick={() => setSpecsDialogOpen(true)}>
            <Settings />
          </IconButton>
        </Tooltip>
        <Tooltip title="Refrescar">
          <IconButton onClick={fetchWidgetData}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </PageHeader>

      {error && (
        <Fade in>
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        </Fade>
      )}
      {success && (
        <Fade in>
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        </Fade>
      )}

      {/* Stats Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <Chip label={`${products.length} Productos`} color="primary" />
        <Chip label={`${orderedSpecs.length} Specs Visibles`} color="secondary" />
        <Chip label={`${availableSpecs.length} Specs Totales`} variant="outlined" />
        <Chip 
          label={widgetData.isActive ? 'Activo' : 'Inactivo'} 
          color={widgetData.isActive ? 'success' : 'default'}
        />
        <Box sx={{ flex: 1 }} />
        <Tabs
          value={viewMode}
          onChange={(e, newValue) => setViewMode(newValue)}
          sx={{ minHeight: 36 }}
        >
          <Tab 
            value="editor" 
            label="Editor" 
            icon={<TableChart sx={{ fontSize: 18 }} />} 
            iconPosition="start"
            sx={{ minHeight: 36, py: 0 }}
          />
          <Tab 
            value="completeness" 
            label="Completitud" 
            icon={<Healing sx={{ fontSize: 18 }} />} 
            iconPosition="start"
            sx={{ minHeight: 36, py: 0 }}
          />
          <Tab 
            value="upselling" 
            label="Upselling" 
            icon={<TrendingUp sx={{ fontSize: 18 }} />} 
            iconPosition="start"
            sx={{ minHeight: 36, py: 0 }}
          />
          <Tab 
            value="preview" 
            label="Preview" 
            icon={<Preview sx={{ fontSize: 18 }} />} 
            iconPosition="start"
            sx={{ minHeight: 36, py: 0 }}
          />
        </Tabs>
      </Box>

      {/* View Content */}
      {viewMode === 'preview' ? (
        /* Widget Preview */
        <Card>
          <CardContent>
            <WidgetPreview
              products={products}
              specsToShow={orderedSpecs}
              specsLabels={comparisonConfig.specsLabels || {}}
              title="Leve sua experiência para o próximo nível"
            />
          </CardContent>
        </Card>
      ) : viewMode === 'completeness' ? (
        /* Completeness Inspector */
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Healing color="primary" />
              Inspector de Completitud de Specs
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              Esta vista muestra qué propiedades faltan en cada producto. Puedes agregar o editar specs manualmente para completar la información.
            </Alert>
            
            {/* Summary Stats */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 200 }}>
                <Typography variant="caption" color="text.secondary">Total Specs Detectadas</Typography>
                <Typography variant="h4">{getAllSpecKeys().length}</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 200 }}>
                <Typography variant="caption" color="text.secondary">Productos</Typography>
                <Typography variant="h4">{products.length}</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 200 }}>
                <Typography variant="caption" color="text.secondary">Completitud Promedio</Typography>
                <Typography variant="h4">
                  {products.length > 0 
                    ? Math.round(products.reduce((acc, p) => acc + getProductSpecCompleteness(p).percentage, 0) / products.length)
                    : 0}%
                </Typography>
              </Paper>
            </Box>

            {/* Specs Coverage Table */}
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Warning color="warning" />
              Cobertura por Spec (ordenadas por faltantes)
            </Typography>
            
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Spec</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Cobertura</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Productos sin esta spec</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getAllSpecKeys()
                    .map(key => ({ key, ...getSpecCoverage(key) }))
                    .sort((a, b) => a.percentage - b.percentage)
                    .map(({ key, count, total, percentage, missingProducts }) => (
                      <TableRow key={key} sx={{ bgcolor: percentage < 100 ? 'warning.50' : 'inherit' }}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {percentage === 100 ? (
                              <CheckCircle color="success" fontSize="small" />
                            ) : percentage >= 50 ? (
                              <Warning color="warning" fontSize="small" />
                            ) : (
                              <Error color="error" fontSize="small" />
                            )}
                            <Typography variant="body2" fontWeight={500}>
                              {getSpecLabel(key)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ({key})
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ flex: 1, maxWidth: 100 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={percentage} 
                                color={percentage === 100 ? 'success' : percentage >= 50 ? 'warning' : 'error'}
                                sx={{ height: 8, borderRadius: 4 }}
                              />
                            </Box>
                            <Typography variant="body2">
                              {count}/{total} ({percentage}%)
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {missingProducts.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {missingProducts.slice(0, 3).map(p => (
                                <Chip 
                                  key={p.sku} 
                                  label={p.sku} 
                                  size="small" 
                                  variant="outlined"
                                  color="warning"
                                  onClick={() => openAddSpecDialog(p, key)}
                                  sx={{ cursor: 'pointer' }}
                                />
                              ))}
                              {missingProducts.length > 3 && (
                                <Chip label={`+${missingProducts.length - 3} más`} size="small" />
                              )}
                            </Box>
                          ) : (
                            <Chip label="Completo" size="small" color="success" />
                          )}
                        </TableCell>
                        <TableCell>
                          {missingProducts.length > 0 && (
                            <Tooltip title="Completar faltantes">
                              <Button 
                                size="small" 
                                startIcon={<AddCircle />}
                                onClick={() => openAddSpecDialog(missingProducts[0], key)}
                              >
                                Completar
                              </Button>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Products Completeness */}
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Healing color="primary" />
              Completitud por Producto
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 2 }}>
              {products
                .map(p => ({ product: p, ...getProductSpecCompleteness(p) }))
                .sort((a, b) => a.percentage - b.percentage)
                .map(({ product, total, complete, missing, missingKeys, percentage }) => (
                  <Paper
                    key={product.sku}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderColor: percentage === 100 ? 'success.main' : percentage >= 70 ? 'warning.main' : 'error.main',
                      borderWidth: 2,
                    }}
                  >
                    {/* Product Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <Box
                        component="img"
                        src={product.image}
                        sx={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 1 }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" noWrap fontWeight={600}>
                          {product.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {product.sku}
                        </Typography>
                      </Box>
                      <Chip
                        label={`${percentage}%`}
                        size="small"
                        color={percentage === 100 ? 'success' : percentage >= 70 ? 'warning' : 'error'}
                      />
                    </Box>
                    
                    {/* Progress */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption">{complete} de {total} specs</Typography>
                        <Typography variant="caption" color={missing > 0 ? 'error.main' : 'success.main'}>
                          {missing > 0 ? `${missing} faltantes` : 'Completo'}
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={percentage} 
                        color={percentage === 100 ? 'success' : percentage >= 70 ? 'warning' : 'error'}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                    
                    {/* Missing Specs */}
                    {missingKeys.length > 0 && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Specs faltantes (click para agregar):
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {missingKeys.map(key => (
                            <Chip
                              key={key}
                              label={getSpecLabel(key)}
                              size="small"
                              color="error"
                              variant="outlined"
                              icon={<AddCircle />}
                              onClick={() => openAddSpecDialog(product, key)}
                              sx={{ cursor: 'pointer' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                    
                    {/* Add New Spec Button */}
                    <Box sx={{ mt: 2 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={() => openAddSpecDialog(product)}
                        fullWidth
                      >
                        Agregar Nueva Spec
                      </Button>
                    </Box>
                  </Paper>
                ))}
            </Box>
          </CardContent>
        </Card>
      ) : viewMode === 'upselling' ? (
        /* Upselling Map View */
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUp color="primary" />
              Mapa de Recomendaciones de Upselling
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              Cuando un usuario visita la página de un producto, se le mostrarán los productos recomendados configurados aquí.
              Los productos sin recomendaciones no mostrarán el widget de upselling.
            </Alert>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: 2 }}>
              {products.map((product) => {
                const recommendations = upsellingMap[product.sku] || [];
                const recommendedProducts = recommendations
                  .map(sku => products.find(p => p.sku === sku))
                  .filter(Boolean);
                
                return (
                  <Paper
                    key={product.sku}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderColor: recommendations.length > 0 ? 'primary.main' : 'divider',
                      borderWidth: recommendations.length > 0 ? 2 : 1,
                    }}
                  >
                    {/* Source Product */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <Box
                        component="img"
                        src={product.image}
                        sx={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 1 }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" noWrap fontWeight={600}>
                          {product.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {product.sku}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Edit />}
                        onClick={() => openUpsellingDialog(product)}
                      >
                        Editar
                      </Button>
                    </Box>
                    
                    {/* Arrow */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <ArrowDownward color="action" fontSize="small" />
                      <Typography variant="caption" color="text.secondary">
                        recomienda {recommendations.length} producto{recommendations.length !== 1 ? 's' : ''}:
                      </Typography>
                    </Box>
                    
                    {/* Recommended Products */}
                    {recommendations.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 2 }}>
                        {recommendedProducts.map((rec, idx) => (
                          <Box
                            key={rec.sku}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              p: 1,
                              bgcolor: 'grey.50',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'grey.200',
                            }}
                          >
                            <Chip label={idx + 1} size="small" color="primary" sx={{ width: 24, height: 24 }} />
                            <Box
                              component="img"
                              src={rec.image}
                              sx={{ width: 32, height: 32, objectFit: 'contain', borderRadius: 0.5 }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" noWrap>{rec.title}</Typography>
                              <Typography variant="caption" color="text.secondary">{rec.sku}</Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Alert severity="warning" sx={{ py: 0.5 }}>
                        Sin recomendaciones - widget no se mostrará
                      </Alert>
                    )}
                  </Paper>
                );
              })}
            </Box>
          </CardContent>
        </Card>
      ) : (
      /* Comparison Table - Editor Mode */
      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 320px)' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell 
                    sx={{ 
                      minWidth: 200, 
                      backgroundColor: 'background.paper',
                      fontWeight: 600,
                      borderRight: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SwapVert fontSize="small" />
                      Especificacion
                    </Box>
                  </TableCell>
                  {products.map((product, index) => (
                  <TableCell
                    key={product.sku}
                    sx={{
                      minWidth: 180,
                      maxWidth: 220,
                      backgroundColor: 'background.paper',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                          {product.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {product.sku}
                        </Typography>
                        {/* Indicators row */}
                        <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {/* Completeness indicator */}
                          {(() => {
                            const comp = getProductSpecCompleteness(product);
                            return (
                              <Tooltip title={comp.missing > 0 ? `${comp.missing} specs faltantes` : 'Specs completas'}>
                                <Chip
                                  size="small"
                                  icon={comp.percentage === 100 ? <CheckCircle sx={{ fontSize: 14 }} /> : <Warning sx={{ fontSize: 14 }} />}
                                  label={`${comp.percentage}%`}
                                  color={comp.percentage === 100 ? 'success' : comp.percentage >= 70 ? 'warning' : 'error'}
                                  variant="outlined"
                                  sx={{ 
                                    height: 20, 
                                    fontSize: 11,
                                    cursor: 'pointer',
                                    '& .MuiChip-icon': { fontSize: 12 }
                                  }}
                                  onClick={() => setViewMode('completeness')}
                                />
                              </Tooltip>
                            );
                          })()}
                          {/* Upselling indicator */}
                          <Chip
                            size="small"
                            icon={<TrendingUp sx={{ fontSize: 14 }} />}
                            label={`${(upsellingMap[product.sku] || []).length} rec.`}
                            color={(upsellingMap[product.sku] || []).length > 0 ? 'primary' : 'default'}
                            variant={(upsellingMap[product.sku] || []).length > 0 ? 'filled' : 'outlined'}
                            sx={{ 
                              height: 20, 
                              fontSize: 11,
                              cursor: 'pointer',
                              '& .MuiChip-icon': { fontSize: 12 }
                            }}
                            onClick={() => openUpsellingDialog(product)}
                          />
                          {/* Specs edit button */}
                          <Tooltip title="Agregar/Editar Spec">
                            <Chip
                              size="small"
                              icon={<Edit sx={{ fontSize: 12 }} />}
                              label="Spec"
                              color="secondary"
                              variant="outlined"
                              sx={{ 
                                height: 20, 
                                fontSize: 11,
                                cursor: 'pointer',
                                '& .MuiChip-icon': { fontSize: 12 }
                              }}
                              onClick={() => openAddSpecDialog(product)}
                            />
                          </Tooltip>
                        </Box>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          setMenuAnchor(e.currentTarget);
                          setSelectedProduct(product);
                        }}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                ))}
                </TableRow>
                
                {/* Product Images Row */}
                <TableRow>
                  <TableCell sx={{ borderRight: '1px solid', borderColor: 'divider', backgroundColor: (theme) => alpha(theme.palette.grey[100], 0.5) }}>
                    <Typography variant="body2" color="text.secondary">Imagen</Typography>
                  </TableCell>
                  {products.map((product) => (
                    <TableCell key={`img-${product.sku}`} sx={{ backgroundColor: (theme) => alpha(theme.palette.grey[100], 0.5) }}>
                      <Box
                        component="img"
                        src={product.image}
                        alt={product.title}
                        sx={{
                          width: 80,
                          height: 80,
                          objectFit: 'contain',
                          backgroundColor: 'white',
                          borderRadius: 1,
                          p: 0.5
                        }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
                
                {/* Price Row */}
                <TableRow>
                  <TableCell sx={{ borderRight: '1px solid', borderColor: 'divider', fontWeight: 500 }}>
                    Precio
                  </TableCell>
                  {products.map((product) => (
                    <TableCell key={`price-${product.sku}`}>
                      {renderEditableCell(product, 'price', product.price)}
                    </TableCell>
                  ))}
                </TableRow>
                
                {/* Stock Row */}
                <TableRow>
                  <TableCell sx={{ borderRight: '1px solid', borderColor: 'divider', fontWeight: 500 }}>
                    Stock
                  </TableCell>
                  {products.map((product) => (
                    <TableCell key={`stock-${product.sku}`}>
                      <Chip 
                        label={product.in_stock === 'in stock' ? 'Disponible' : 'Sin stock'}
                        size="small"
                        color={product.in_stock === 'in stock' ? 'success' : 'error'}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              
              <TableBody>
                <DragDropContext onDragEnd={handleSpecReorder}>
                  <Droppable droppableId="specs">
                    {(provided) => (
                      <tbody ref={provided.innerRef} {...provided.droppableProps}>
                        {orderedSpecs.map((specKey, index) => (
                          <Draggable key={specKey} draggableId={specKey} index={index}>
                            {(provided, snapshot) => (
                              <TableRow
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                sx={{
                                  backgroundColor: snapshot.isDragging 
                                    ? (theme) => alpha(theme.palette.primary.main, 0.05)
                                    : index % 2 === 0 ? 'background.paper' : (theme) => alpha(theme.palette.grey[50], 0.5),
                                }}
                              >
                                <TableCell
                                  sx={{
                                    borderRight: '1px solid',
                                    borderColor: 'divider',
                                    fontWeight: 500
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box {...provided.dragHandleProps} sx={{ cursor: 'grab' }}>
                                      <DragIndicator fontSize="small" color="action" />
                                    </Box>
                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                      {getSpecLabel(specKey)}
                                    </Typography>
                                    <Tooltip title="Ocultar spec">
                                      <IconButton
                                        size="small"
                                        onClick={() => toggleSpecVisibility(specKey)}
                                      >
                                        <VisibilityOff fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                                {products.map((product) => (
                                  <TableCell key={`${specKey}-${product.sku}`}>
                                    {renderEditableCell(product, `specs.${specKey}`, product.specs?.[specKey])}
                                  </TableCell>
                                ))}
                              </TableRow>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </tbody>
                    )}
                  </Droppable>
                </DragDropContext>
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Add New Row Button */}
          <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setAddRowDialogOpen(true)}
            >
              Agregar Fila de Comparación
            </Button>
            <Button
              variant="outlined"
              startIcon={<Settings />}
              onClick={() => setSpecsDialogOpen(true)}
            >
              Configurar Specs Visibles
            </Button>
          </Box>
        </CardContent>
      </Card>
      )}

      {/* Product Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem 
          onClick={() => handleMoveProduct(selectedProduct?.sku, -1)}
          disabled={products.findIndex(p => p.sku === selectedProduct?.sku) === 0}
        >
          <ListItemIcon><ArrowBack fontSize="small" /></ListItemIcon>
          <ListItemText>Mover a la izquierda</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={() => handleMoveProduct(selectedProduct?.sku, 1)}
          disabled={products.findIndex(p => p.sku === selectedProduct?.sku) === products.length - 1}
        >
          <ListItemIcon><ArrowForward fontSize="small" /></ListItemIcon>
          <ListItemText>Mover a la derecha</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => {
          openUpsellingDialog(selectedProduct);
          setMenuAnchor(null);
        }}>
          <ListItemIcon><TrendingUp fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText primary="Configurar Upselling" secondary={`${(upsellingMap[selectedProduct?.sku] || []).length} recomendados`} />
        </MenuItem>
        <MenuItem onClick={() => {
          openAddSpecDialog(selectedProduct);
          setMenuAnchor(null);
        }}>
          <ListItemIcon><Settings fontSize="small" color="secondary" /></ListItemIcon>
          <ListItemText primary="Editar Specs" secondary={`${Object.keys(selectedProduct?.specs || {}).length} especificaciones`} />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => {
          window.open(selectedProduct?.link, '_blank');
          setMenuAnchor(null);
        }}>
          <ListItemIcon><OpenInNew fontSize="small" /></ListItemIcon>
          <ListItemText>Ver en tienda</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          navigator.clipboard.writeText(selectedProduct?.sku || '');
          setCopySnackbar(true);
          setMenuAnchor(null);
        }}>
          <ListItemIcon><ContentCopy fontSize="small" /></ListItemIcon>
          <ListItemText>Copiar SKU</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem 
          onClick={() => handleRemoveProduct(selectedProduct?.sku)}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Eliminar del widget</ListItemText>
        </MenuItem>
      </Menu>

      {/* Specs Configuration Dialog */}
      <Dialog
        open={specsDialogOpen}
        onClose={() => setSpecsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Configurar Especificaciones</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Selecciona las especificaciones que quieres mostrar en la tabla de comparacion.
            Las especificaciones ocultas no se mostraran.
          </Typography>
          <List>
            {availableSpecs.map((spec) => {
              const isHidden = comparisonConfig.hiddenSpecs?.includes(spec.key);
              return (
                <ListItem
                  key={spec.key}
                  secondaryAction={
                    <Switch
                      checked={!isHidden}
                      onChange={() => toggleSpecVisibility(spec.key)}
                    />
                  }
                  sx={{
                    backgroundColor: isHidden ? (theme) => alpha(theme.palette.grey[200], 0.5) : 'transparent',
                    borderRadius: 1,
                    mb: 0.5
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1">{spec.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Key: {spec.key} | Tipo: {spec.type}
                    </Typography>
                  </Box>
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSpecsDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Upselling Configuration Dialog */}
      <Dialog
        open={upsellingDialogOpen}
        onClose={() => setUpsellingDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUp color="primary" />
          Configurar Upselling
        </DialogTitle>
        <DialogContent>
          {editingUpsellingProduct && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Cuando un usuario vea <strong>{editingUpsellingProduct.title}</strong> ({editingUpsellingProduct.sku}), 
                se le recomendarán los productos seleccionados a continuación.
              </Alert>
              
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Productos a recomendar (en orden de prioridad):
              </Typography>
              
              {/* Selected recommendations with order */}
              {editingRecommendations.length > 0 && (
                <Paper variant="outlined" sx={{ mb: 2, p: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Orden de recomendación (arrastra para reordenar):
                  </Typography>
                  {editingRecommendations.map((sku, index) => {
                    const product = products.find(p => p.sku === sku);
                    return (
                      <Box
                        key={sku}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          p: 1,
                          mb: 0.5,
                          bgcolor: 'primary.50',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'primary.200',
                        }}
                      >
                        <Chip label={index + 1} size="small" color="primary" />
                        <Box
                          component="img"
                          src={product?.image}
                          sx={{ width: 32, height: 32, objectFit: 'contain', borderRadius: 0.5 }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight={500}>{product?.title}</Typography>
                          <Typography variant="caption" color="text.secondary">{sku}</Typography>
                        </Box>
                        <IconButton size="small" onClick={() => moveRecommendation(index, -1)} disabled={index === 0}>
                          <ArrowUpward fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => moveRecommendation(index, 1)} disabled={index === editingRecommendations.length - 1}>
                          <ArrowDownward fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => toggleRecommendation(sku)} color="error">
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Paper>
              )}
              
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Seleccionar productos para recomendar:
              </Typography>
              
              <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                <List dense>
                  {products
                    .filter(p => p.sku !== editingUpsellingProduct.sku)
                    .map((product) => {
                      const isSelected = editingRecommendations.includes(product.sku);
                      return (
                        <ListItem
                          key={product.sku}
                          button
                          onClick={() => toggleRecommendation(product.sku)}
                          sx={{
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            bgcolor: isSelected ? 'primary.50' : 'transparent',
                          }}
                        >
                          <Checkbox
                            checked={isSelected}
                            size="small"
                            sx={{ mr: 1 }}
                          />
                          <Box
                            component="img"
                            src={product.image}
                            sx={{ width: 40, height: 40, objectFit: 'contain', mr: 1.5, borderRadius: 0.5 }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <ListItemText
                            primary={product.title}
                            secondary={product.sku}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                          {isSelected && (
                            <Chip 
                              label={`#${editingRecommendations.indexOf(product.sku) + 1}`} 
                              size="small" 
                              color="primary" 
                            />
                          )}
                        </ListItem>
                      );
                    })}
                </List>
              </Paper>
              
              {editingRecommendations.length === 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Sin recomendaciones configuradas. El widget no se mostrará para este producto.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpsellingDialogOpen(false)}>Cancelar</Button>
          <Button 
            variant="contained" 
            onClick={saveRecommendations}
            disabled={saving}
            startIcon={<Save />}
          >
            Guardar Recomendaciones
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Spec Dialog */}
      <Dialog
        open={addSpecDialogOpen}
        onClose={() => setAddSpecDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddCircle color="primary" />
          Agregar Spec a {editingSpecProduct?.title}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Agrega una nueva propiedad al producto. Este valor se guardará como override local del widget.
          </Alert>
          
          <TextField
            fullWidth
            label="Nombre de la Spec (key)"
            value={specFormData.key}
            onChange={(e) => setSpecFormData(prev => ({ ...prev, key: e.target.value }))}
            placeholder="ej: garantia, velocidad_impresion"
            sx={{ mb: 2 }}
            helperText="Usa snake_case para el nombre (ej: duplex_automatico)"
          />
          
          <TextField
            fullWidth
            select
            label="Tipo de valor"
            value={specFormData.type}
            onChange={(e) => setSpecFormData(prev => ({ ...prev, type: e.target.value, value: '' }))}
            sx={{ mb: 2 }}
          >
            <MenuItem value="string">Texto</MenuItem>
            <MenuItem value="boolean">Booleano (Sí/No)</MenuItem>
            <MenuItem value="object">Objeto JSON</MenuItem>
          </TextField>
          
          {specFormData.type === 'boolean' ? (
            <TextField
              fullWidth
              select
              label="Valor"
              value={specFormData.value}
              onChange={(e) => setSpecFormData(prev => ({ ...prev, value: e.target.value }))}
            >
              <MenuItem value="true">Sí (true)</MenuItem>
              <MenuItem value="false">No (false)</MenuItem>
            </TextField>
          ) : specFormData.type === 'object' ? (
            <TextField
              fullWidth
              label="Valor (JSON)"
              value={specFormData.value}
              onChange={(e) => setSpecFormData(prev => ({ ...prev, value: e.target.value }))}
              multiline
              rows={4}
              placeholder={'{\n  "negro": "Até 30 ppm",\n  "color": "Até 24 ppm"\n}'}
              helperText="Ingresa un objeto JSON válido"
            />
          ) : (
            <TextField
              fullWidth
              label="Valor"
              value={specFormData.value}
              onChange={(e) => setSpecFormData(prev => ({ ...prev, value: e.target.value }))}
              placeholder="ej: Até 2 Anos"
            />
          )}
          
          {/* Suggest from existing specs */}
          {specFormData.key === '' && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Sugerencias de specs existentes:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {getAllSpecKeys()
                  .filter(key => !editingSpecProduct?.specs?.[key])
                  .slice(0, 8)
                  .map(key => (
                    <Chip
                      key={key}
                      label={getSpecLabel(key)}
                      size="small"
                      variant="outlined"
                      onClick={() => setSpecFormData(prev => ({ ...prev, key }))}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddSpecDialogOpen(false)}>Cancelar</Button>
          <Button 
            variant="contained" 
            onClick={() => saveSpec(true)}
            disabled={saving || !specFormData.key || !specFormData.value}
            startIcon={<Save />}
          >
            Agregar Spec
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Spec Dialog */}
      <Dialog
        open={editSpecDialogOpen}
        onClose={() => setEditSpecDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Edit color="primary" />
          Editar "{getSpecLabel(editingSpecKey || '')}" en {editingSpecProduct?.title}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Este valor sobrescribirá el valor original importado. El cambio se guardará como override local del widget.
          </Alert>
          
          <TextField
            fullWidth
            label="Spec"
            value={specFormData.key}
            disabled
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            select
            label="Tipo de valor"
            value={specFormData.type}
            onChange={(e) => setSpecFormData(prev => ({ ...prev, type: e.target.value }))}
            sx={{ mb: 2 }}
          >
            <MenuItem value="string">Texto</MenuItem>
            <MenuItem value="boolean">Booleano (Sí/No)</MenuItem>
            <MenuItem value="object">Objeto JSON</MenuItem>
          </TextField>
          
          {specFormData.type === 'boolean' ? (
            <TextField
              fullWidth
              select
              label="Valor"
              value={specFormData.value}
              onChange={(e) => setSpecFormData(prev => ({ ...prev, value: e.target.value }))}
            >
              <MenuItem value="true">Sí (true)</MenuItem>
              <MenuItem value="false">No (false)</MenuItem>
            </TextField>
          ) : specFormData.type === 'object' ? (
            <TextField
              fullWidth
              label="Valor (JSON)"
              value={specFormData.value}
              onChange={(e) => setSpecFormData(prev => ({ ...prev, value: e.target.value }))}
              multiline
              rows={4}
            />
          ) : (
            <TextField
              fullWidth
              label="Valor"
              value={specFormData.value}
              onChange={(e) => setSpecFormData(prev => ({ ...prev, value: e.target.value }))}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditSpecDialogOpen(false)}>Cancelar</Button>
          <Button 
            variant="contained" 
            onClick={() => saveSpec(false)}
            disabled={saving || !specFormData.value}
            startIcon={<Save />}
          >
            Guardar Cambios
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Specs Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={cancelImport}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudDownload color="secondary" />
            Importar Especificaciones
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Step 1: Select SKUs */}
          {importStep === 'select' && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Selecciona los productos para los cuales deseas importar especificaciones desde la API externa.
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2">
                  {selectedSkusForImport.length} de {products.length} productos seleccionados
                </Typography>
                <Button size="small" onClick={toggleAllSkus}>
                  {selectedSkusForImport.length === products.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
                </Button>
              </Box>

              <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                <List dense>
                  {products.map((product) => (
                    <ListItem
                      key={product.sku}
                      button
                      onClick={() => toggleSkuForImport(product.sku)}
                      sx={{
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Checkbox
                        checked={selectedSkusForImport.includes(product.sku)}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {product.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          SKU: {product.sku}
                        </Typography>
                      </Box>
                      {product.specs && Object.keys(product.specs).length > 0 && (
                        <Chip 
                          label={`${Object.keys(product.specs).length} specs`} 
                          size="small" 
                          color="info"
                          variant="outlined"
                        />
                      )}
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Box>
          )}

          {/* Step 2: Loading */}
          {importStep === 'loading' && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Importando especificaciones...
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Obteniendo datos de la API externa
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={importProgress} 
                sx={{ height: 8, borderRadius: 4, mb: 2 }}
              />
              <Typography variant="body2" color="text.secondary">
                {importProgress}% completado
              </Typography>
            </Box>
          )}

          {/* Step 3: Select Fields */}
          {importStep === 'fields' && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Selecciona los campos que deseas importar (maximo 60). Estos campos se agregarán a las especificaciones de los productos.
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2">
                  {selectedImportFields.length} de {importFields.length} campos seleccionados
                </Typography>
                <Chip 
                  label={`Maximo 60`} 
                  size="small" 
                  color={selectedImportFields.length >= 60 ? 'error' : 'default'}
                />
              </Box>

              <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
                <List dense>
                  {importFields.map((field) => (
                    <ListItem
                      key={field}
                      button
                      onClick={() => toggleFieldForImport(field)}
                      sx={{
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: selectedImportFields.includes(field) 
                          ? (theme) => alpha(theme.palette.primary.main, 0.05)
                          : 'transparent'
                      }}
                    >
                      <Checkbox
                        checked={selectedImportFields.includes(field)}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          key: {field}
                        </Typography>
                        {importFieldExamples[field] && importFieldExamples[field].length > 0 && (
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.5 }}
                          >
                            Ej: {importFieldExamples[field].slice(0, 2).join(', ')}
                          </Typography>
                        )}
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Box>
          )}

          {/* Step 4: Applying */}
          {importStep === 'applying' && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Aplicando especificaciones...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Guardando {selectedImportFields.length} campos en {selectedSkusForImport.length} productos
              </Typography>
              <LinearProgress sx={{ mt: 3 }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {importStep === 'select' && (
            <>
              <Button onClick={cancelImport}>Cancelar</Button>
              <Button 
                variant="contained" 
                onClick={startImportSpecs}
                disabled={selectedSkusForImport.length === 0}
                startIcon={<CloudDownload />}
              >
                Importar ({selectedSkusForImport.length} SKUs)
              </Button>
            </>
          )}
          {importStep === 'loading' && (
            <Button onClick={cancelImport} color="error">
              Cancelar
            </Button>
          )}
          {importStep === 'fields' && (
            <>
              <Button onClick={cancelImport}>Cancelar</Button>
              <Button 
                variant="contained" 
                onClick={finalizeImport}
                disabled={selectedImportFields.length === 0}
                startIcon={<Check />}
              >
                Aplicar {selectedImportFields.length} campos
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Add Comparison Row Dialog */}
      <Dialog
        open={addRowDialogOpen}
        onClose={() => setAddRowDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Add color="primary" />
          Agregar Nueva Fila de Comparación
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3, mt: 1 }}>
            Agrega una nueva fila de especificación. Puedes ingresar un valor diferente para cada producto.
            Los campos vacíos se mostrarán como "-".
          </Alert>
          
          <TextField
            fullWidth
            label="Nombre del Campo *"
            value={newRowData.key}
            onChange={(e) => setNewRowData(prev => ({ ...prev, key: e.target.value }))}
            placeholder="ej: garantia, velocidad_impresion, conectividad"
            helperText="Se convertirá a formato snake_case automáticamente"
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            label="Etiqueta para Mostrar"
            value={newRowData.label}
            onChange={(e) => setNewRowData(prev => ({ ...prev, label: e.target.value }))}
            placeholder="ej: Garantía, Velocidad de Impresión, Conectividad"
            helperText="Si no se especifica, se usará el nombre del campo formateado"
            sx={{ mb: 2 }}
          />
          
          {/* Suggestions from existing specs */}
          {availableSpecs.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Specs existentes que podrías agregar:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {availableSpecs
                  .filter(s => !getOrderedSpecs().includes(s.key))
                  .slice(0, 10)
                  .map(spec => (
                    <Chip
                      key={spec.key}
                      label={spec.label}
                      size="small"
                      variant="outlined"
                      onClick={() => setNewRowData(prev => ({ ...prev, key: spec.key, label: spec.label }))}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
              </Box>
            </Box>
          )}
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Valores por Producto:
          </Typography>
          
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            {products.map((product) => (
              <Box 
                key={product.sku} 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2, 
                  mb: 1.5,
                  p: 1,
                  borderRadius: 1,
                  bgcolor: 'grey.50'
                }}
              >
                <Box
                  component="img"
                  src={product.image}
                  alt={product.title}
                  sx={{ 
                    width: 40, 
                    height: 40, 
                    objectFit: 'contain',
                    borderRadius: 0.5,
                    bgcolor: 'white'
                  }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap fontWeight={500}>
                    {product.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {product.sku}
                  </Typography>
                </Box>
                <TextField
                  size="small"
                  placeholder="- (vacío)"
                  value={newRowData.productValues[product.sku] || ''}
                  onChange={(e) => setNewRowData(prev => ({
                    ...prev,
                    productValues: { ...prev.productValues, [product.sku]: e.target.value }
                  }))}
                  sx={{ width: 180 }}
                />
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddRowDialogOpen(false);
            setNewRowData({ key: '', label: '', productValues: {} });
          }}>
            Cancelar
          </Button>
          <Button 
            variant="contained" 
            onClick={addComparisonRow}
            disabled={saving || !newRowData.key.trim()}
            startIcon={saving ? null : <Add />}
          >
            {saving ? 'Agregando...' : 'Agregar Fila'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy Snackbar */}
      <Snackbar
        open={copySnackbar}
        autoHideDuration={2000}
        onClose={() => setCopySnackbar(false)}
        message="Copiado al portapapeles"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default WidgetEditor;
